"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { X, Check, ChartBar as BarChart3, Sparkles, Search, Bot, Database, ArrowRight, Cpu, Layers, ShieldCheck, Zap } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const guessworkPoints = ["Relying on outdated traditional SEO rules that AI search engines ignore", "Zero visibility into whether ChatGPT or Perplexity recommends your business", "Manual checks with no citation tracking or structured data verification", "Hoping prospective clients stumble upon generic directory listings"];
const dataDrivenPoints = ["Real-time citation tracking across ChatGPT, Gemini, and Perplexity", "Custom LLM knowledge graph and machine-readable schema architecture", "Automated monthly visibility audits and actionable ranking insights", "Guaranteed brand authority positioning when AI answers local queries"];
const ecosystemNodes = [{
  title: "ChatGPT Optimization",
  desc: "Train OpenAI models to cite your services directly",
  icon: Bot
}, {
  title: "Perplexity Citations",
  desc: "Dominate real-time answer engine search results",
  icon: Search
}, {
  title: "Structured Data",
  desc: "Machine-readable schema and knowledge graphs",
  icon: Database
}, {
  title: "LLM Indexing",
  desc: "Direct prompt-level authority and index placement",
  icon: Cpu
}, {
  title: "Gemini Optimization",
  desc: "Capture Google AI search recommendations",
  icon: Sparkles
}];
const statsData = [{
  value: "3.4x",
  label: "Average Citation Boost",
  subtext: "Within 60 days of deployment"
}, {
  value: "89%",
  label: "First-Choice AI Answers",
  subtext: "Across targeted local queries"
}, {
  value: "100%",
  label: "Verified Data Accuracy",
  subtext: "Eliminating hallucinated info"
}];
export default function Why() {
  const shouldReduceMotion = useReducedMotion();
  const [activeNode, setActiveNode] = useState<number | null>(0);
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="whyitmattersfinalcta" data-nav="light" className="bg-background text-foreground relative py-20 lg:py-28 overflow-hidden">
      {}
      <div className="absolute inset-0 pointer-events-none opacity-20 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-[var(--accent-deep-red)]/20 via-transparent to-transparent" />

      <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10 space-y-20 lg:space-y-28">
        {}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.3
      }} variants={containerVariants} className="text-center max-w-3xl mx-auto space-y-4">
          <motion.div variants={itemVariants} className="flex justify-center">
            <Badge className="bg-[var(--accent-deep-red)]/15 text-[var(--accent-deep-red)] border-[var(--accent-deep-red)]/30 font-mono uppercase tracking-widest px-3.5 py-1 text-xs rounded-full">
              WHY IT MATTERS
            </Badge>
          </motion.div>
          <motion.h2 variants={itemVariants} className="text-3xl md:text-5xl font-default font-bold tracking-tight text-foreground">
            Stop guessing. Start dominating AI search answers.
          </motion.h2>
          <motion.p variants={itemVariants} className="text-muted-foreground text-lg leading-relaxed font-default">
            When potential customers ask AI engines who to hire in your city, conventional SEO is no longer enough. You need precise, machine-readable authority infrastructure.
          </motion.p>
        </motion.div>

        {}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={containerVariants} className="grid grid-cols-1 md:grid-cols-2 gap-8 items-stretch">
          {}
          <motion.div variants={itemVariants} className="h-full">
            <Card className="h-full border-border bg-card/50 shadow-sm rounded-xl flex flex-col justify-between">
              <CardHeader className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Traditional Path</span>
                  <X className="size-5 text-muted-foreground" />
                </div>
                <CardTitle className="text-2xl font-bold font-default text-foreground">
                  Guesswork &amp; Hope
                </CardTitle>
                <CardDescription className="text-muted-foreground">
                  How most businesses lose invisible market share in modern AI search.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 pt-4">
                <Separator className="bg-border/60" />
                <ul className="space-y-3 pt-2">
                  {guessworkPoints.map((point, i) => <li data-index={i} key={i} className="flex items-start gap-3">
                      <div className="mt-1 rounded-full p-0.5 bg-muted text-muted-foreground shrink-0">
                        <X className="size-4" />
                      </div>
                      <span className="text-sm text-muted-foreground leading-snug">{point}</span>
                    </li>)}
                </ul>
              </CardContent>
            </Card>
          </motion.div>

          {}
          <motion.div variants={itemVariants} className="h-full">
            <Card className="h-full border-2 border-[var(--accent-deep-red)] bg-card shadow-lg rounded-xl flex flex-col justify-between relative overflow-hidden">
              <div className="absolute top-0 right-0 bg-[var(--accent-deep-red)] text-white font-mono text-[10px] uppercase font-bold tracking-wider px-3 py-1 rounded-bl-lg">
                Engine v2.4 Active
              </div>
              <div>
                <CardHeader className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="border-[var(--accent-deep-red)]/40 text-[var(--accent-deep-red)] bg-[var(--accent-deep-red)]/10 text-xs">
                      ClearLine Methodology
                    </Badge>
                  </div>
                  <CardTitle className="text-2xl font-bold font-default text-foreground flex items-center gap-2">
                    Data-Driven Visibility
                  </CardTitle>
                  <CardDescription className="text-muted-foreground">
                    Engineered precision that turns AI recommendations into high-intent leads.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4 pt-4">
                  <Separator className="bg-border" />
                  <ul className="space-y-3 pt-2">
                    {dataDrivenPoints.map((point, i) => <li data-index={i} key={i} className="flex items-start gap-3">
                        <div className="mt-1 rounded-full p-0.5 bg-[var(--accent-deep-red)]/15 text-[var(--accent-deep-red)] shrink-0">
                          <Check className="size-4" />
                        </div>
                        <span className="text-sm font-medium text-foreground leading-snug">{point}</span>
                      </li>)}
                  </ul>
                </CardContent>
              </div>
              <div className="p-6 pt-0">
                <div className="p-3 bg-muted/50 border border-border rounded-lg flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="size-5 text-[var(--accent-deep-red)]" />
                    <span className="text-xs font-semibold text-foreground">Verified AI Readiness Guarantee</span>
                  </div>
                  <Zap className="size-4 text-[var(--accent-deep-red)]" />
                </div>
              </div>
            </Card>
          </motion.div>
        </motion.div>

        {}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={containerVariants} className="space-y-8">
          <div className="text-center space-y-2 max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold font-default text-foreground">
              Connected GEO Architecture
            </h3>
            <p className="text-sm text-muted-foreground">
              Hover a service node to explore how our central analytics hub feeds intelligence across all AI engines.
            </p>
          </div>

          <div className="p-6 lg:p-10 rounded-2xl bg-card border border-border shadow-sm">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              {}
              <div className="lg:col-span-5 flex flex-col items-center justify-center text-center p-8 rounded-xl bg-muted/40 border border-border relative">
                <div className="size-16 rounded-2xl bg-[var(--accent-deep-red)] text-white flex items-center justify-center shadow-md mb-4">
                  <BarChart3 className="size-8" />
                </div>
                <Badge className="bg-[var(--accent-deep-red)]/15 text-[var(--accent-deep-red)] border-none mb-2 font-mono text-xs">
                  Central Hub
                </Badge>
                <h4 className="text-xl font-bold font-default text-foreground">ClearLine GEO Analytics</h4>
                <p className="text-xs text-muted-foreground mt-2 max-w-xs">
                  Continuous multi-engine prompt testing, citation monitoring, and real-time knowledge graph updates.
                </p>
                <div className="mt-6 flex items-center gap-2">
                  <Button size="sm" asChild>
                    
                  </Button>
                </div>
              </div>

              {}
              <div className="lg:col-span-7 space-y-3">
                {ecosystemNodes.map((node, i) => {
                const NodeIcon = node.icon;
                const isActive = activeNode === i;
                return <motion.div data-index={i} key={i} whileHover={{
                  x: shouldReduceMotion ? 0 : 4
                }} className="group" onMouseEnter={() => setActiveNode(i)}>
                      <div className={`flex items-center justify-between p-4 rounded-xl border transition-all duration-200 cursor-default select-none ${isActive ? "bg-[var(--accent-deep-red)]/5 border-[var(--accent-deep-red)] shadow-xs" : "bg-background border-border hover:border-[var(--accent-deep-red)]/50"}`}>
                        <div className="flex items-center gap-4">
                          <div className={`size-10 rounded-lg flex items-center justify-center transition-colors ${isActive ? "bg-[var(--accent-deep-red)] text-white" : "bg-muted text-foreground"}`}>
                            <NodeIcon className="size-5" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-sm text-foreground group-hover:text-[var(--accent-deep-red)] transition-colors">
                                {node.title}
                              </span>
                              <Badge variant="outline" className="text-[10px] py-0 px-1.5 h-4 border-border text-muted-foreground">
                                GEO Module
                              </Badge>
                            </div>
                            <p className="text-xs text-muted-foreground mt-0.5">{node.desc}</p>
                          </div>
                        </div>
                        <ArrowRight className={`size-4 transition-transform ${isActive ? "text-[var(--accent-deep-red)] translate-x-1" : "text-muted-foreground"}`} />
                      </div>
                    </motion.div>;
              })}
              </div>
            </div>
          </div>
        </motion.div>

        {}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.3
      }} variants={containerVariants} className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {statsData.map((stat, i) => <motion.div data-index={i} key={i} variants={itemVariants} className="p-6 rounded-xl bg-card border border-border text-center space-y-2 shadow-xs">
              <div className="text-4xl md:text-5xl font-extrabold font-default text-[var(--accent-deep-red)] tracking-tight">
                {stat.value}
              </div>
              <div className="text-base font-semibold text-foreground">{stat.label}</div>
              <p className="text-xs text-muted-foreground">{stat.subtext}</p>
            </motion.div>)}
        </motion.div>

        {}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.3
      }} variants={itemVariants} className="rounded-2xl text-white p-8 md:p-12 lg:p-16 relative overflow-hidden shadow-xl bg-[var(--accent-deep-red)]">
          {}
          <div className="absolute -right-20 -bottom-20 size-80 rounded-full bg-white/10 blur-3xl pointer-events-none" />
          <div className="absolute -left-20 -top-20 size-80 rounded-full bg-black/20 blur-3xl pointer-events-none" />

          <div className="relative z-10 max-w-3xl mx-auto text-center space-y-6">
            <Badge className="inline-flex items-center justify-center border font-medium w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden border-transparent [a&]:hover:bg-primary/90 text-white border-none px-3.5 py-1 text-xs font-mono uppercase tracking-widest rounded-full !bg-black/30 dark:aria-invalid:ring-destructive/40">
              TAKE THE NEXT STEP
            </Badge>
            <h3 className="text-3xl md:text-5xl font-bold font-default tracking-tight text-white">
              Ready to ensure AI engines recommend your business?
            </h3>
            <p className="text-white/80 text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
              Get ahead of your competitors before AI search defaults to them. Schedule a direct strategy session or request your free visibility checkup.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
              <Button size="lg" className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm transition-all [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive shadow-xs h-10 rounded-md has-[>svg]:px-4 font-semibold w-full px-8 !text-[var(--accent-deep-red)] bg-white hover:bg-white/90 disabled:pointer-events-none disabled:opacity-50 dark:aria-invalid:ring-destructive/40 sm:w-auto" asChild>
                <Link to="/PricingPlansPage">Get started now</Link>
              </Button>
              <Button size="lg" variant="outline" className="border-white/40 text-white hover:bg-white/10 font-semibold w-full sm:w-auto px-8" asChild>
                <Link to="/VisibilityCheckupPage">
                  Free AI visibility checkup
                </Link>
              </Button>
            </div>
            <div className="flex items-center justify-center gap-6 pt-2 text-xs text-white/70">
              <span className="flex items-center gap-1.5">
                <Check className="size-3.5 text-white" /> No credit card required
              </span>
              <span className="flex items-center gap-1.5">
                <Check className="size-3.5 text-white" /> 48-hour report delivery
              </span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}